import { useInfiniteQuery, useQueryClient, InfiniteData } from '@tanstack/react-query';
import { getMessages } from '../resources/api.resource';
import { IMessage } from '../models/Message';
import store from '../utils/store';

export interface UseConversationMessagesOptions {
  collaborationId: string | undefined;
  enabled?: boolean;
}

export interface UseConversationMessagesResult {
  messages: IMessage[];
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
  refetch: () => void;
  hasMore: boolean;
  fetchNextPage: () => void;
  isFetchingNextPage: boolean;
}

interface MessagesPage {
  messages: IMessage[];
  hasMore: boolean;
  continuationToken: string | null;
}

export const MESSAGES_QUERY_KEY = (collaborationId: string) =>
  ['conversationMessages', collaborationId] as const;

/**
 * Custom hook to fetch and cache conversation messages with pagination support.
 * Scrolling up loads older pages via continuation token.
 */
export function useConversationMessages({
  collaborationId,
  enabled = true,
}: UseConversationMessagesOptions): UseConversationMessagesResult {
  // Determine if we should refetch based on unreadCount
  const shouldRefetch = (): boolean => {
    if (!collaborationId) return false;

    const collaborations = store.getState().collaborations;
    const collaboration = collaborations.find(
      (c: any) => c.collaborationId === collaborationId
    );

    const hasUnreadMessages = collaboration?.unreadCount > 0;

    return hasUnreadMessages;
  };

  const query = useInfiniteQuery<MessagesPage, Error>({
    queryKey: MESSAGES_QUERY_KEY(collaborationId!),

    queryFn: async ({ pageParam }) => {
      if (!collaborationId) {
        throw new Error('collaborationId is required');
      }
      // pageParam is undefined on first page, token string on subsequent pages
      const token = typeof pageParam === 'string' && pageParam !== '' ? pageParam : undefined;
      return getMessages(collaborationId, token);
    },

    initialPageParam: undefined as string | undefined,

    // '' sentinel: hasMore=true but token not yet received — keeps hasNextPage=true
    // in TanStack v5 without returning null (null would set hasNextPage=false)
    getNextPageParam: (lastPage) =>
      lastPage.hasMore ? (lastPage.continuationToken ?? '') : undefined,

    enabled: enabled && !!collaborationId,

    refetchOnMount: shouldRefetch,
  });

  // Pages are ordered newest-first (page[0] = most recent).
  // Reverse so older pages come first for chronological display.
  const messages: IMessage[] = query.data
    ? [...query.data.pages].reverse().flatMap((p) => p.messages)
    : [];

  return {
    messages,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error as Error | null,
    refetch: query.refetch,
    hasMore: query.hasNextPage ?? false,
    fetchNextPage: query.fetchNextPage,
    isFetchingNextPage: query.isFetchingNextPage,
  };
}

/**
 * Helper to add a new message to the cache without refetching.
 * Appends to the first (most recent) page so the message appears at the bottom.
 */
export function addMessageToCache(
  queryClient: ReturnType<typeof useQueryClient>,
  collaborationId: string,
  newMessage: IMessage
): void {
  queryClient.setQueryData<InfiniteData<MessagesPage>>(
    MESSAGES_QUERY_KEY(collaborationId),
    (old) => {
      if (!old) {
        return {
          pages: [{ messages: [newMessage], hasMore: false, continuationToken: null }],
          pageParams: [undefined],
        };
      }

      const [firstPage, ...restPages] = old.pages;

      // Prevent duplicates
      if (firstPage.messages.some((m) => m.id === newMessage.id)) return old;

      return {
        ...old,
        pages: [
          { ...firstPage, messages: [...firstPage.messages, newMessage] },
          ...restPages,
        ],
      };
    }
  );
}

/**
 * Helper to invalidate a conversation cache, forcing a refetch.
 */
export function invalidateConversationCache(
  queryClient: ReturnType<typeof useQueryClient>,
  collaborationId: string
): void {
  queryClient.invalidateQueries({
    queryKey: MESSAGES_QUERY_KEY(collaborationId),
  });
}

/**
 * Helper to clear all conversation caches (e.g., on logout).
 */
export function clearAllConversationCaches(
  queryClient: ReturnType<typeof useQueryClient>
): void {
  queryClient.removeQueries({
    queryKey: ['conversationMessages'],
  });
}

/**
 * Helper to replace a pending message with the confirmed message from the server.
 * Searches the first (most recent) page, where optimistic messages are appended.
 */
export function replacePendingMessage(
  queryClient: ReturnType<typeof useQueryClient>,
  collaborationId: string,
  confirmedMessage: IMessage
): void {
  queryClient.setQueryData<InfiniteData<MessagesPage>>(
    MESSAGES_QUERY_KEY(collaborationId),
    (old) => {
      if (!old) {
        return {
          pages: [{ messages: [confirmedMessage], hasMore: false, continuationToken: null }],
          pageParams: [undefined],
        };
      }

      const [firstPage, ...restPages] = old.pages;

      // Already confirmed — no-op
      const alreadyExists = firstPage.messages.some(
        (m) => m.id === confirmedMessage.id && !m.isPending
      );
      if (alreadyExists) return old;

      let replaced = false;
      const updatedMessages = firstPage.messages.map((msg) => {
        if (!replaced && msg.isPending && msg.sender.id === confirmedMessage.sender.id) {
          replaced = true;
          return confirmedMessage;
        }
        return msg;
      });

      return {
        ...old,
        pages: [
          {
            ...firstPage,
            messages: replaced ? updatedMessages : [...updatedMessages, confirmedMessage],
          },
          ...restPages,
        ],
      };
    }
  );
}

/**
 * Helper to update a message's reactions in the cache.
 * Searches all pages for the target message.
 */
export function updateMessageReactionInCache(
  queryClient: ReturnType<typeof useQueryClient>,
  collaborationId: string,
  messageId: string,
  reactions: Record<string, any>
): void {
  queryClient.setQueryData<InfiniteData<MessagesPage>>(
    MESSAGES_QUERY_KEY(collaborationId),
    (old) => {
      if (!old) return old;

      return {
        ...old,
        pages: old.pages.map((page) => ({
          ...page,
          messages: page.messages.map((m) =>
            m.id === messageId
              ? { ...m, interactions: { ...m.interactions, reactions } }
              : m
          ),
        })),
      };
    }
  );
}
